import React, { useEffect, useState } from 'react';
import { RelatedReports as IRelatedReports } from 'app/concierge/types';
import { Box, Stack } from '@mui/material';
import { conciergeAPI, ITileInfoData } from 'api/concierge';
import DatabaseIcon from 'components/common/icon/DatabaseIcon';
import IconMi from 'components/common/icon/IconMi';
import { TileInfoIcon } from 'components/tile-info-icon';
import { getViewUrl } from 'tools/tools';
import { openExternalView } from 'app/element';
import useBundleTranslation from 'i18n';
import { useMiNavigation } from 'hooks/useNavigation';
import { useSelector } from 'react-redux';
import { dispatchCustomEvent } from 'tools/events';

interface IProps {
    data: IRelatedReports;
}

export default function RelatedReports({ data }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const navigate = useMiNavigation();
    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;
    const [tilesInfo, setTilesInfo] = useState<ITileInfoData[]>([]);

    useEffect(() => {
        if (!data.elements.length) return;
        const ids = data.elements.map((r) => `${r.element_id}_0`);
        conciergeAPI.getTilesInfo(ids).then(setTilesInfo);
    }, [data.elements]);

    if (!tilesInfo.length) return null;

    const onChipClick = async (tileInfo: ITileInfoData) => {
        if (isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'item-click',
                payload: { info: tileInfo },
            });
            return;
        }

        const elementId = tileInfo.element_id;
        const segmentLinkChunk = Number(tileInfo.segment_value_id) > 0 ? `segment/${tileInfo.segment_value_id}` : '';
        const viewUrl = getViewUrl(tileInfo.element_type as string);
        const elementLink = `${viewUrl}${elementId}/${segmentLinkChunk}`;

        if (isMobile) {
            window.location.href = elementLink;
        } else if ('external' == tileInfo.external_report_display) {
            await openExternalView(Number(tileInfo.element_id), tileInfo.segment_value_id ?? 0);
        } else {
            navigate(elementLink);
        }
    };

    return (
        <Stack gap="4px" sx={{ mt: 1 }}>
            <Box sx={{ fontSize: '12px', color: 'text.secondary' }}>{t('message.related_reports')}</Box>
            <Stack direction="row" flexWrap="wrap" gap="4px">
                {tilesInfo.map((tileInfo, index) => {
                    const reportingTool = tileInfo.reporting_tool_name || tileInfo.external_content_name || '';
                    const name = tileInfo.element_dashboard_name || '';
                    const id = `${data.elements[index]?.element_id}_0`;

                    return (
                        <TileInfoIcon
                            key={id}
                            tile={tileInfo}
                            customFieldsDisplayOnTileInfo={tileInfo.customFieldsDisplayOnTileInfo}
                        >
                            <Stack
                                direction="row"
                                alignItems="flex-start"
                                gap="8px"
                                onClick={() => onChipClick(tileInfo)}
                                sx={{
                                    boxSizing: 'border-box',
                                    p: '6px 10px 6px 6px',
                                    height: '28px',
                                    borderRadius: '5px',
                                    border: '1px solid rgba(34, 34, 34, 0.16)',
                                    flex: 'none',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                            >
                                {reportingTool ? (
                                    <DatabaseIcon name={reportingTool} />
                                ) : (
                                    <IconMi sx={{ fontSize: 16 }} icon="metric" />
                                )}
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        lineHeight: '18px',
                                        color: '#075B7E',
                                        flex: 'none',
                                    }}
                                >
                                    {name}
                                </Box>
                            </Stack>
                        </TileInfoIcon>
                    );
                })}
            </Stack>
        </Stack>
    );
}
